import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Send, MessageCircle } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useSite } from "@/context/SiteContext";
import { api, BACKEND, errMsg, waLink } from "@/lib/api";
import { RequestForm } from "@/components/site/RequestForm";
import { Suggestions } from "@/components/site/ConsultantSuggestions";
import { Price, WatchImage, AvailBadge } from "@/components/site/WatchCard";

const Rich = ({ text }) => text.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <strong key={i} className="font-medium text-[#E6D5AC]">{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>));

function Cards({ watches, onNav }) {
  const { t, r } = useT();
  return (
    <div className="grid grid-cols-2 gap-3 mt-3" data-testid="advisor-watch-cards">
      {watches.map((w) => (
        <Link key={w.id} to={`${r.watch}/${w.slug}`} onClick={onNav} className="group block bg-[#0C0B09] border border-[#282520] hover:border-[#C8AE6E]/50 transition-colors duration-500" data-testid={`advisor-card-${w.slug}`}>
          <WatchImage image={w.image} alt={w.model} size="sm" />
          <div className="p-3 space-y-1">
            <p className="text-[9px] uppercase tracking-[0.25em] text-[#C8AE6E]">{w.brand}</p>
            <p className="font-display text-sm leading-tight text-[#F3EFE6]">{w.model}</p>
            <Price w={w} className="text-sm" />
            <div className="pt-1 scale-90 origin-left"><AvailBadge value={w.availability} /></div>
            <span className="block text-[10px] tracking-[0.2em] uppercase text-[#C8AE6E] pt-1">{t.adv_view} →</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

function ContactStep({ convId, onDone, salutation }) {
  const { t } = useT();
  return (
    <div className="bg-[#0C0B09] border border-[#282520] p-4 mt-2" data-testid="advisor-contact-step">
      <p className="text-sm text-[#F3EFE6]/85 mb-3">{t.adv_contact_q}</p>
      <RequestForm type="contact" source="consultor" conversationId={convId} compact prefill={{ salutation, message: t.adv_contact_msg }} testid="advisor-contact-form" onDone={(d) => onDone("given", d)} />
      <button onClick={() => onDone("declined")} className="mt-3 text-xs tracking-[0.2em] uppercase text-[#A39E93] hover:text-[#E6D5AC]" data-testid="advisor-contact-not-now">{t.adv_not_now}</button>
    </div>
  );
}

export function Consultant() {
  const { t, lang } = useT();
  const { settings, advisor, openAdvisor, closeAdvisor } = useSite();
  const [conv, setConv] = useState(null);
  const [step, setStep] = useState("idle");
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sal, setSal] = useState(null);
  const pendingWatch = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs, step]);

  useEffect(() => {
    if (!advisor.open) return;
    if (advisor.watch) pendingWatch.current = advisor.watch;
    if (!conv && step === "idle") {
      setStep("loading");
      api.post("/consultant/start", { lang }).then(({ data }) => {
        setConv(data);
        setMsgs([{ role: "notice", text: data.notice }, { role: "assistant", text: data.welcome }]);
        setStep("salutation");
      }).catch((e) => { setError(errMsg(e, t.adv_unavailable)); setStep("error"); });
    } else if (step === "chat" && pendingWatch.current) sendWatchIntro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advisor.nonce, advisor.open]);

  const profile = (body) => api.post(`/consultant/${conv.id}/profile`, body).catch(() => {});

  const chooseSal = (k) => {
    setSal(k);
    profile({ salutation: k });
    setMsgs((m) => [...m, { role: "user", text: t.sal[k] }]);
    setStep("contact");
  };

  const addWa = (d) => d?.whatsapp_url && setMsgs((m) => [...m, { role: "action", kind: "whatsapp", url: d.whatsapp_url }]);
  const contactDone = (status, d) => {
    profile({ contact_status: status });
    addWa(d);
    setStep("chat");
    if (pendingWatch.current) setTimeout(sendWatchIntro, 50);
  };

  function sendWatchIntro() {
    const w = pendingWatch.current;
    pendingWatch.current = null;
    send(t.adv_hello_watch(`${w.brand} ${w.model}`), w.id);
  }

  async function send(text, watchId = null) {
    if (!text.trim() || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "", streaming: true }]);
    const patchLast = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    const extras = [];
    const pushExtra = (x) => extras.push(x);
    try {
      const res = await fetch(`${BACKEND}/api/consultant/${conv.id}/message`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, watch_id: watchId }) });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || t.adv_unavailable);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.t === "delta") patchLast((l) => ({ ...l, text: l.text + ev.c }));
          else if (ev.t === "cards") pushExtra({ role: "cards", watches: ev.watches });
          else if (ev.t === "action") pushExtra({ role: "action", ...ev });
          else if (ev.t === "error") patchLast((l) => ({ ...l, text: l.text || ev.c, error: true }));
        }
      }
    } catch (e) {
      patchLast((l) => ({ ...l, text: e.message, error: true }));
    } finally {
      setMsgs((m) => [...m.filter((x) => !(x.role === "assistant" && !x.text && x.streaming)).map((x) => ({ ...x, streaming: false })), ...extras]);
      setBusy(false);
    }
  }

  if (!settings?.ai?.enabled) return null;

  return (
    <>
      {!advisor.open && (
        <button onClick={() => openAdvisor()} className="fixed bottom-5 right-5 z-50 flex items-center gap-3 rounded-full pl-1.5 pr-5 py-1.5 bg-[#161512]/90 backdrop-blur-xl border border-[#C8AE6E]/40 hover:border-[#C8AE6E] shadow-[0_10px_40px_rgba(0,0,0,.6)] transition-[border-color,transform] duration-500 hover:-translate-y-0.5" data-testid="advisor-open-button">
          <img src="/emblem-square.png" alt="" className="w-11 h-11 rounded-full" />
          <span className="text-xs tracking-[0.2em] uppercase text-[#E6D5AC] hidden sm:inline">{t.adv_short}</span>
        </button>
      )}
      {advisor.open && (
        <div className="fixed z-50 inset-0 h-[100dvh] sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[420px] sm:h-[min(680px,calc(100vh-40px))] flex flex-col bg-[#0F0E0C] sm:border border-[#C8AE6E]/25 shadow-[0_30px_80px_rgba(0,0,0,.7)] reveal" role="dialog" aria-label={t.adv_title} data-testid="advisor-panel">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#282520]">
            <img src="/emblem-square.png" alt="" className="w-9 h-9 rounded-full" />
            <div className="flex-1">
              <p className="font-display text-[15px] text-[#F3EFE6]">{t.adv_title}</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8AE6E]">{t.adv_ai}</p>
            </div>
            <a href={waLink(settings.whatsapp_number, t.wa_default)} target="_blank" rel="noopener noreferrer" className="p-2 text-[#A39E93] hover:text-[#E6D5AC]" aria-label={t.adv_human} title={t.adv_human} data-testid="advisor-human-whatsapp"><MessageCircle className="w-4 h-4" /></a>
            <button onClick={closeAdvisor} className="p-2 text-[#A39E93] hover:text-[#E6D5AC]" aria-label="Fechar" data-testid="advisor-close-button"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4" data-testid="advisor-messages">
            {step === "loading" && <p className="text-sm text-[#A39E93] animate-pulse">…</p>}
            {step === "error" && <p className="text-sm text-[#e0937f]" data-testid="advisor-error">{error}</p>}
            {msgs.map((m, i) => {
              if (m.role === "notice") return <div key={i} className="text-xs text-[#A39E93] border border-[#C8AE6E]/25 p-3 leading-relaxed" data-testid="advisor-notice-message"><span className="eyebrow block mb-1">{lang === "en" ? "AI · AI Act" : "IA · AI Act"}</span>{m.text}</div>;
              if (m.role === "cards") return <Cards key={i} watches={m.watches} onNav={() => window.innerWidth < 640 && closeAdvisor()} />;
              if (m.role === "action") {
                if (m.kind === "whatsapp") return <a key={i} href={m.url} target="_blank" rel="noopener noreferrer" className="btn-gold w-full" data-testid="advisor-whatsapp-action"><MessageCircle className="w-4 h-4" />{t.adv_open_wa}</a>;
                if (m.kind === "contact_offer") return <ContactStep key={i} convId={conv.id} salutation={sal} onDone={(s, d) => { profile({ contact_status: s }); addWa(d); }} />;
                if (m.kind === "form") return (
                  <div key={i} className="bg-[#0C0B09] border border-[#C8AE6E]/30 p-4" data-testid={`advisor-form-${m.form}`}>
                    <p className="eyebrow mb-4">{{ order: t.order_wa, purchase: t.buy_wa, reservation: t.reserve, waitlist: t.waitlist, specific: t.concierge_eyebrow }[m.form] || t.request_contact}</p>
                    <RequestForm type={m.form} source="consultor" conversationId={conv.id} compact prefill={{ ...m.prefill, salutation: sal }} testid={`advisor-${m.form}-form`} />
                  </div>
                );
              }
              return (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[88%] text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#C8AE6E]/12 border border-[#C8AE6E]/25 text-[#F3EFE6] px-4 py-2.5 rounded-2xl rounded-br-sm" : `text-[#F3EFE6]/90 ${m.error ? "text-[#e0937f]" : ""}`}`} data-testid={m.role === "user" ? "advisor-user-message" : "advisor-assistant-message"}>
                    <Rich text={m.text} />{m.streaming && <span className="inline-block w-1.5 h-3.5 ml-0.5 bg-[#C8AE6E] animate-pulse align-middle" />}
                  </div>
                </div>
              );
            })}
            {step === "salutation" && (
              <div className="space-y-3" data-testid="advisor-salutation-step">
                <p className="text-sm text-[#F3EFE6]/90">{t.adv_sal_q}</p>
                <div className="flex flex-wrap gap-2">
                  {["sr", "sra"].map((k) => <button key={k} onClick={() => chooseSal(k)} className="btn-line !px-5 !py-2 text-xs" data-testid={`advisor-salutation-${k}`}>{t.sal[k]}</button>)}
                </div>
              </div>
            )}
            {step === "contact" && <ContactStep convId={conv.id} salutation={sal} onDone={contactDone} />}
            <div ref={endRef} />
          </div>
          {step === "chat" && <Suggestions items={settings.ai.suggestions} lang={lang} disabled={busy} onSend={(q) => send(q)} sizeQ={t.adv_sizes} />}
          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className={`flex items-center gap-2 px-4 py-3 ${step === "chat" && settings.ai.suggestions?.length ? "" : "border-t border-[#282520]"}`}>
            <input value={input} onChange={(e) => setInput(e.target.value)} disabled={step !== "chat" || busy} placeholder={t.adv_placeholder} maxLength={1500} className="flex-1 bg-transparent text-base sm:text-sm outline-none placeholder:text-[#6f6a60] disabled:opacity-40" data-testid="advisor-chat-input" />
            <button type="submit" disabled={step !== "chat" || busy || !input.trim()} className="w-10 h-10 rounded-full bg-[#C8AE6E] text-[#0C0B09] flex items-center justify-center disabled:opacity-30 transition-opacity" aria-label={t.adv_send} data-testid="advisor-send-button"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
}
